import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import type { RevealApi } from 'reveal.js';
import { usePresentationContext } from '../core/PresentationContext';

const isLocalhost =
  window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

// The slide id label is fully derived from the reveal.js deck's current
// slide, so it is read straight from that external system via
// useSyncExternalStore instead of being duplicated into local state.
const useSlideId = (deck: RevealApi | null, isInitialized: boolean): string => {
  const subscribe = useCallback(
    (callback: () => void) => {
      if (!deck || !isInitialized) return () => {};
      deck.on('slidechanged', callback);
      return () => {
        deck.off('slidechanged', callback);
      };
    },
    [deck, isInitialized]
  );

  const getSnapshot = useCallback((): string => {
    if (!deck || !isInitialized) return '';
    const currentSlide = deck.getCurrentSlide();
    if (!currentSlide) return '';
    const indices = deck.getIndices();
    return `${indices.h + 1}.${indices.v + 1}`;
  }, [deck, isInitialized]);

  return useSyncExternalStore(subscribe, getSnapshot);
};

const styles = {
  container: {
    position: 'fixed' as const,
    top: '10px',
    right: '10px',
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    color: '#00ff00',
    padding: '10px',
    fontFamily: 'monospace',
    fontSize: '14px',
    zIndex: 9999,
    borderRadius: '4px',
    pointerEvents: 'none' as const,
  },
};

export const DebugOverlay = () => {
  const { deck, isInitialized } = usePresentationContext();
  const [startTime] = useState(() => Date.now());
  const [totalTime, setTotalTime] = useState(0);
  const [slideTime, setSlideTime] = useState(0);
  const slideId = useSlideId(deck, isInitialized);
  // slideStartTime is genuinely event-driven (it marks when a 'slidechanged'
  // fired), so it stays as state updated from the subscription callback -
  // that's the case react-hooks/set-state-in-effect explicitly allows.
  const [slideStartTime, setSlideStartTime] = useState(() => Date.now());

  // Handle slide changes
  useEffect(() => {
    if (!deck || !isInitialized) return;

    const handleSlideChange = () => {
      setSlideStartTime(Date.now());
    };

    deck.on('slidechanged', handleSlideChange);

    return () => {
      deck.off('slidechanged', handleSlideChange);
    };
  }, [deck, isInitialized]);

  // Handle timing
  useEffect(() => {
    if (!isLocalhost || !deck || !isInitialized) return;

    const timer = setInterval(() => {
      const now = Date.now();
      setTotalTime(now - startTime);
      setSlideTime(now - slideStartTime);
    }, 100);

    return () => {
      clearInterval(timer);
    };
  }, [deck, isInitialized, slideStartTime, startTime]);

  if (!isLocalhost) return null;

  const formatTime = (ms: number) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    const tenths = Math.floor((ms % 1000) / 100);
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}.${tenths}`;
  };

  return (
    <div style={styles.container}>
      <div>Slide: {formatTime(slideTime)}</div>
      <div>Total: {formatTime(totalTime)}</div>
      <div>ID: {slideId}</div>
    </div>
  );
};
